import { useCallback, useEffect, useRef, useState } from "react";

export type VrboActionStatus = 'idle' | 'success' | 'error'

export type VrboAction = {
    run: () => Promise<void>
    clear: () => void
    isLoading: boolean
    status: VrboActionStatus
    result: any
    completedAt: Date | null
}

type VrboActionOptions = {
    // used in the console error message, e.g. "updating VRBO property pricing"
    errorLabel: string
    // transform a successful response before it is stored (e.g. JSON.parse)
    parse?: (response: any) => any
    // when false, run() does nothing
    enabled?: boolean
    // called after a successful request with the raw response
    onSuccess?: (response: any) => void
}

// Runs a VRBO request and keeps its loading state and result (response or error) together.
export default function useVrboAction(
    request: () => Promise<any>,
    options: VrboActionOptions,
): VrboAction {
    const { errorLabel, parse, enabled = true, onSuccess } = options

    const [isLoading, setIsLoading] = useState(false)
    const [status, setStatus] = useState<VrboActionStatus>('idle')
    const [result, setResult] = useState<any>(null)
    const [completedAt, setCompletedAt] = useState<Date | null>(null)

    const isMounted = useRef(true)
    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    // keep the latest closures without forcing callers to memoize
    const requestRef = useRef(request)
    requestRef.current = request
    const parseRef = useRef(parse)
    parseRef.current = parse
    const onSuccessRef = useRef(onSuccess)
    onSuccessRef.current = onSuccess

    const run = useCallback(async () => {
        if (!enabled) return
        setIsLoading(true)
        try {
            const response = await requestRef.current()
            const parsed = parseRef.current ? parseRef.current(response) : response
            if (isMounted.current) {
                setResult(parsed)
                setStatus('success')
            }
            onSuccessRef.current?.(response)
        } catch (error) {
            console.error(`Error ${errorLabel}:`, error)
            if (!isMounted.current) return
            setResult(error)
            setStatus('error')
        } finally {
            if (isMounted.current) {
                setCompletedAt(new Date())
                setIsLoading(false)
            }
        }
    }, [enabled, errorLabel])

    const clear = useCallback(() => {
        setResult(null)
        setStatus('idle')
        setCompletedAt(null)
    }, [])

    return { run, clear, isLoading, status, result, completedAt }
}
